import { httpPost, httpGet, setToken, clearToken } from '@/services/http';
import type { Permission, RoleKey } from '@/utils/rbac';
import { encryptPassword } from '@/utils/crypto';

export interface LoginPayload {
  email: string;
  password: string;
  encrypted?: boolean; // 是否已加密
}

export interface BackendUserInfo {
  id: string;
  name: string;
  email: string;
  department?: string;
  role_ids: string[];
  role_names: string[];
  permissions: string[];
}

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  roles: RoleKey[];
  permissions: Permission[];
  department?: string;
  status: 'active' | 'inactive';
}

/** 转换后端用户信息为前端格式 */
function transformUser(backendUser: BackendUserInfo): CurrentUser {
  // 安全处理 permissions，避免 null 导致的问题
  const permissions = backendUser.permissions && backendUser.permissions.includes('*') 
    ? ['*'] 
    : (backendUser.permissions || []);
  
  return {
    id: backendUser.id,
    name: backendUser.name,
    email: backendUser.email,
    department: backendUser.department,
    roles: backendUser.role_ids as RoleKey[],
    permissions: permissions as Permission[],
    status: 'active',
  };
}

export interface LoginResponse {
  token: string;
  user: BackendUserInfo; // 后端返回的用户信息
}

/** POST /api/auth/login */
export async function apiLogin(payload: LoginPayload): Promise<{ token: string; user: CurrentUser }> {
  try {
    console.log('📤 发送登录请求 (RSA加密密码)');
    
    // 使用 RSA 公钥加密密码
    const encryptedPassword = await encryptPassword(payload.password);
    console.log('🔐 密码已加密:', encryptedPassword.substring(0, 50) + '...');
    
    const res = await httpPost<LoginResponse>('/auth/login', {
      email: payload.email,
      password: encryptedPassword,  // 发送加密后的密码
      encrypted: true,               // 标记为已加密
    });
    console.log('📦 后端响应:', res);
    
    // 确保 res 是正确的格式
    if (!res.token || !res.user) {
      console.error('❌ 响应格式错误:', res);
      throw new Error('响应格式错误：缺少 token 或 user');
    }
    
    setToken(res.token);
    console.log('✅ Token 已保存到 localStorage');
    
    const user = transformUser(res.user);
    console.log('✅ 用户信息转换成功:', user);
    
    return {
      token: res.token,
      user,
    };
  } catch (error) {
    console.error('❌ 登录失败:', error);
    throw error;
  }
}

/** GET /api/auth/me */
export async function apiGetMe(): Promise<CurrentUser> {
  const backendUser = await httpGet<BackendUserInfo>('/auth/me');
  return transformUser(backendUser);
}

/** POST /api/auth/logout */
export async function apiLogout(): Promise<void> {
  try {
    await httpPost('/auth/logout', {});
  } finally {
    clearToken();
  }
}
